import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import { Link } from 'react-router-dom';
import { 
  FiPlus, 
  FiX, 
  FiUser, 
  FiFileText, 
  FiCalendar, 
  FiTag, 
  FiLock,
  FiCheck,
  FiInfo,
  FiChevronDown,
  FiActivity,
  FiBell,
  FiClock,
  FiUsers,
  FiAward,
  FiAlertTriangle,
  FiSave
} from 'react-icons/fi';
import { 
  createDetailTreatmentFromExisting,
  validateTreatmentData, 
  validateObjectId 
} from '../../components/PatientTreatmentDetails/services/detailTreatmentService';
import { API_BASE_URL, API_DOCTOR_LIST, API_PATIENT_LIST } from "../../helpers/urlHelper";

const EnhancedTreatmentForm = ({ onTreatmentCreated, preSelectedPatientId = null }) => {
  const navigate = useNavigate();
  
  // Off-canvas state
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  
  // Data loading states
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [availableTreatments, setAvailableTreatments] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [loadingTreatments, setLoadingTreatments] = useState(false);
  
  // Dropdown states
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const [showDoctorDropdown, setShowDoctorDropdown] = useState(false);
  const [showTreatmentDropdown, setShowTreatmentDropdown] = useState(false);
  
  // Selection states
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [selectedTreatmentTemplate, setSelectedTreatmentTemplate] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    patient_id: preSelectedPatientId || '',
    doctor_id: '',
    treatmentManagementId: '',
    treatment_info: {
      start_date: '',
      estimated_end_date: '',
      status: 'pending',
      progress_percentage: 0
    },
    doctor_info: {
      doctor_name: '',
      doctor_email: '',
      specialty: '',
      license_number: ''
    },
    consent_status: {
      is_required: true,
      is_signed: false,
      pending_signature: true
    },
    next_actions: [],
    active_alerts: []
  });

  // Initialize form with pre-selected patient
  useEffect(() => {
    if (preSelectedPatientId && patients.length > 0) {
      const patient = patients.find(p => p._id === preSelectedPatientId || p.id === preSelectedPatientId);
      if (patient) {
        setSelectedPatient(patient);
        setFormData(prev => ({
          ...prev,
          patient_id: preSelectedPatientId
        }));
      }
    }
  }, [preSelectedPatientId, patients]);

  // Fetch patients
  const fetchPatients = useCallback(async () => {
    setLoadingPatients(true);
    try {
      const response = await fetch(`${API_BASE_URL}${API_PATIENT_LIST}`);
      if (!response.ok) throw new Error('Failed to fetch patients');
      
      const data = await response.json();
      const patientsArray = Array.isArray(data) ? data : data?.data || [];
      setPatients(patientsArray);
    } catch (error) {
      console.error("Error fetching patients:", error);
      setPatients([]);
    } finally {
      setLoadingPatients(false);
    }
  }, []);

  // Fetch doctors
  const fetchDoctors = useCallback(async () => {
    setLoadingDoctors(true);
    try {
      const response = await fetch(`${API_DOCTOR_LIST}`);
      if (!response.ok) throw new Error('Failed to fetch doctors');
      
      const data = await response.json();
      const doctorsArray = data && data.data ? data.data : [];
      setDoctors(doctorsArray);
    } catch (error) {
      console.error('Error fetching doctors:', error);
      setDoctors([]);
    } finally {
      setLoadingDoctors(false);
    }
  }, []);

  // Fetch available treatments
  const fetchAvailableTreatments = useCallback(async () => {
    setLoadingTreatments(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/treatmentManagement`);
      
      const mappedTreatments = response.data.map(treatment => ({
        id: treatment._id || treatment.treatment_id,
        name: treatment.template?.name || 'Unnamed Treatment',
        protocol_code: treatment.template?.protocol_code || 'NO-CODE',
        category: treatment.template?.specialty_id || 'General',
        estimated_duration_days: treatment.phases && treatment.phases.length > 0 
          ? treatment.phases.reduce((total, phase) => total + (phase.estimated_duration_days || 0), 0)
          : 30,
        version: treatment.template?.version || '1.0',
        purpose: treatment.template?.purpose || 'Treatment purpose not specified',
        status: treatment.template?.status || 'active',
        description: treatment.template?.description || 'No description available',
        phases_count: treatment.phases ? treatment.phases.length : 0,
        phases: treatment.phases || [],
        specialty_name: treatment.template?.specialty_name || 'General Medicine',
        created_date: treatment.template?.created_date,
        last_modified: treatment.template?.last_modified
      }));
      
      setAvailableTreatments(mappedTreatments);
    } catch (error) {
      console.error('Error loading available treatments:', error);
      setAvailableTreatments([]);
    } finally {
      setLoadingTreatments(false);
    }
  }, []);

  // Open off-canvas and load data
  const handleOpenOffcanvas = useCallback(async () => {
    setShowOffcanvas(true);
    setCurrentStep(1);
    
    // Load all required data
    await Promise.all([
      fetchPatients(),
      fetchDoctors(),
      fetchAvailableTreatments()
    ]);
  }, [fetchPatients, fetchDoctors, fetchAvailableTreatments]);

  // Close off-canvas and reset form
  const handleCloseOffcanvas = useCallback(() => {
    setShowOffcanvas(false);
    setCurrentStep(1);
    setSelectedPatient(preSelectedPatientId ? selectedPatient : null);
    setSelectedDoctor(null);
    setSelectedTreatmentTemplate(null);
    setShowPatientDropdown(false);
    setShowDoctorDropdown(false);
    setShowTreatmentDropdown(false);
    
    setFormData({
      patient_id: preSelectedPatientId || '',
      doctor_id: '',
      treatmentManagementId: '',
      treatment_info: {
        start_date: '',
        estimated_end_date: '',
        status: 'pending',
        progress_percentage: 0
      },
      doctor_info: {
        doctor_name: '',
        doctor_email: '',
        specialty: '',
        license_number: ''
      },
      consent_status: {
        is_required: true,
        is_signed: false,
        pending_signature: true
      },
      next_actions: [],
      active_alerts: []
    });
  }, [preSelectedPatientId, selectedPatient]);

  // Handle patient selection
  const handlePatientSelect = useCallback((patient) => {
    setSelectedPatient(patient);
    setShowPatientDropdown(false);
    setFormData(prev => ({
      ...prev,
      patient_id: patient._id || patient.id
    }));
  }, []);

  // Handle doctor selection
  const handleDoctorSelect = useCallback((doctor) => {
    setSelectedDoctor(doctor);
    setShowDoctorDropdown(false);
    setFormData(prev => ({
      ...prev,
      doctor_id: doctor._id,
      doctor_info: {
        doctor_name: `${doctor.name} ${doctor.surname}`,
        doctor_email: doctor.email || '',
        specialty: doctor.specialty || '',
        license_number: doctor.license_number || ''
      }
    }));
  }, []);

  // Handle treatment template selection
  const handleTreatmentSelect = useCallback((treatment) => {
    setSelectedTreatmentTemplate(treatment);
    setShowTreatmentDropdown(false);
    
    // Calculate estimated end date
    const startDate = formData.treatment_info.start_date;
    let estimatedEndDate = '';
    
    if (startDate && treatment.estimated_duration_days) {
      const start = new Date(startDate);
      const end = new Date(start.getTime() + (treatment.estimated_duration_days * 24 * 60 * 60 * 1000));
      estimatedEndDate = end.toISOString().split('T')[0];
    }

    setFormData(prev => ({
      ...prev,
      treatmentManagementId: treatment.id,
      treatment_info: {
        ...prev.treatment_info,
        estimated_end_date: estimatedEndDate
      }
    }));
  }, [formData.treatment_info.start_date]);

  // Handle form field changes
  const handleFormChange = useCallback((field, value, section = null) => {
    if (section) {
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
      
      // Auto-calculate end date when start date changes
      if (section === 'treatment_info' && field === 'start_date' && selectedTreatmentTemplate) {
        const estimatedDays = selectedTreatmentTemplate.estimated_duration_days;
        if (value && estimatedDays) {
          const start = new Date(value);
          const end = new Date(start.getTime() + (estimatedDays * 24 * 60 * 60 * 1000));
          const endDateStr = end.toISOString().split('T')[0];
          
          setFormData(prev => ({
            ...prev,
            treatment_info: {
              ...prev.treatment_info,
              [field]: value,
              estimated_end_date: endDateStr
            }
          }));
          return;
        }
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  }, [selectedTreatmentTemplate]);

  // Add next action
  const addNextAction = () => {
    const newAction = {
      action_type: 'consultation',
      description: '',
      scheduled_date: '',
      priority: 'normal',
      completed: false
    };
    
    setFormData(prev => ({
      ...prev,
      next_actions: [...prev.next_actions, newAction]
    }));
  };

  // Remove next action
  const removeNextAction = (index) => {
    setFormData(prev => ({
      ...prev,
      next_actions: prev.next_actions.filter((_, i) => i !== index)
    }));
  };

  // Update next action
  const updateNextAction = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      next_actions: prev.next_actions.map((action, i) => 
        i === index ? { ...action, [field]: value } : action
      )
    }));
  };

  // Add alert
  const addAlert = () => {
    const newAlert = {
      alert_type: 'information',
      message: '',
      priority: 'medium',
      requires_action: false,
      resolved: false,
      created_date: new Date().toISOString()
    };
    
    setFormData(prev => ({
      ...prev,
      active_alerts: [...prev.active_alerts, newAlert]
    }));
  };

  // Remove alert
  const removeAlert = (index) => {
    setFormData(prev => ({
      ...prev,
      active_alerts: prev.active_alerts.filter((_, i) => i !== index)
    }));
  };

  // Update alert
  const updateAlert = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      active_alerts: prev.active_alerts.map((alert, i) => 
        i === index ? { ...alert, [field]: value } : alert
      )
    }));
  };

  // Submit form
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      console.log('🔍 Form validation starting...');
      
      // Basic validations
      if (!formData.patient_id) {
        throw new Error('Please select a patient');
      }
      if (!formData.doctor_id) {
        throw new Error('Please select a doctor');
      }
      if (!formData.treatmentManagementId) {
        throw new Error('Please select a treatment template');
      }
      if (!formData.treatment_info.start_date) {
        throw new Error('Please specify a start date');
      }

      // Validate ObjectIds
      const patientIdError = validateObjectId(formData.patient_id, 'Patient ID');
      if (patientIdError) throw new Error(patientIdError);

      const doctorIdError = validateObjectId(formData.doctor_id, 'Doctor ID');
      if (doctorIdError) throw new Error(doctorIdError);

      const templateIdError = validateObjectId(formData.treatmentManagementId, 'Treatment Template ID');
      if (templateIdError) throw new Error(templateIdError);

      // Prepare treatment data
      const treatmentData = {
        treatmentManagementId: formData.treatmentManagementId,
        patient_id: formData.patient_id,
        doctor_id: formData.doctor_id,
        doctor_info: formData.doctor_info,
        treatment_info: formData.treatment_info,
        consent_status: formData.consent_status,
        next_actions: formData.next_actions,
        active_alerts: formData.active_alerts
      };

      // Validate complete data
      const validationErrors = validateTreatmentData(treatmentData);
      if (validationErrors.length > 0) {
        throw new Error('Data validation failed:\n' + validationErrors.join('\n'));
      }

      console.log('🚀 Creating treatment...');
      const result = await createDetailTreatmentFromExisting(treatmentData);
      
      console.log('🎉 Treatment created successfully!', result);
      window.location.reload();
      // Success feedback
      alert('✅ Treatment created successfully!\n\n' + 
            `Patient: ${selectedPatient?.name}\n` +
            `Doctor: ${selectedDoctor ? `${selectedDoctor.name} ${selectedDoctor.surname}` : 'Assigned Doctor'}\n` +
            `Treatment: ${selectedTreatmentTemplate?.name}\n` +
            `Status: ${formData.treatment_info.status}`);
      
      // Close off-canvas
      handleCloseOffcanvas();
      
      // Notify parent component
      if (onTreatmentCreated && typeof onTreatmentCreated === 'function') {
        onTreatmentCreated(result);
      }
      
    } catch (error) {
      console.error('❌ Error creating treatment:', error);
      
      let userMessage = 'An unexpected error occurred while creating the treatment.';
      
      if (error.message.includes('Patient ID') || error.message.includes('Doctor ID') || error.message.includes('Template ID')) {
        userMessage = '❌ Invalid selection. Please refresh the page and try again.';
      } else if (error.message.includes('select')) {
        userMessage = '❌ ' + error.message;
      } else if (error.message.includes('validation failed')) {
        userMessage = '❌ Form validation failed:\n' + error.message.replace('Data validation failed:', '');
      } else {
        userMessage = '❌ Server error: ' + error.message;
      }
      
      alert(userMessage);
      
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, selectedPatient, selectedDoctor, selectedTreatmentTemplate, handleCloseOffcanvas, onTreatmentCreated]);

  // Form validation
  const isStep1Valid = selectedPatient && selectedDoctor && selectedTreatmentTemplate;
  const isStep2Valid = formData.treatment_info.start_date;
  const isFormValid = isStep1Valid && isStep2Valid;

  // Steps configuration
  const steps = [
    { 
      number: 1, 
      title: 'Select', 
      description: 'Patient, Doctor, Treatment',
      isValid: isStep1Valid
    },
    { 
      number: 2, 
      title: 'Details', 
      description: 'Dates & Configuration',
      isValid: isStep2Valid
    },
    { 
      number: 3, 
      title: 'Review', 
      description: 'Confirm & Create',
      isValid: isFormValid
    }
  ];

  return (
    <>
      {/* Add New Button */}
       <Link to="#" 
          className="btn btn-primary addnewbtn d-flex align-items-center gap-1" 
          onClick={handleOpenOffcanvas}
        >
         <span className="d-block material-symbols-outlined icon-md">add</span>
        <span className="d-block">Add New</span>
    </Link>

      {/* Off-canvas */}
      {showOffcanvas && (
        <>
          {/* Backdrop */}
          <div 
            className="offcanvas-backdrop fade show" 
            onClick={handleCloseOffcanvas}
            style={{ zIndex: 1045 }}
          ></div>
          
          {/* Off-canvas Panel */}
          <div 
            className="offcanvas offcanvas-end show d-flex flex-column" 
            tabIndex="-1" 
            style={{ 
              width: '500px', 
              maxWidth: '90vw',
              zIndex: 1050,
              height: '100vh'
            }}
          >
            {/* Compact Header */}
            <div className="offcanvas-header border-bottom bg-primary flex-shrink-0 py-2">
              <div className="d-flex align-items-center gap-2">
                <FiFileText className="text-white" size={18} />
                <div>
                  <h6 className="offcanvas-title fw-bold text-white mb-0">New Treatment</h6>
                  <small className="text-white text-opacity-75">Step {currentStep}/3</small>
                </div>
              </div>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={handleCloseOffcanvas}
                aria-label="Close"
              ></button>
            </div>
            
            {/* Compact Progress Steps */}
            <div className="px-2 py-1 bg-light border-bottom flex-shrink-0">
              <div className="d-flex justify-content-between align-items-center">
                {steps.map((step) => (
                  <div key={step.number} className="d-flex align-items-center" style={{ flex: '1 1 0%' }}>
                    <div className="d-flex flex-column align-items-center text-center">
                      <div 
                        className={`rounded-circle d-flex align-items-center justify-content-center mb-1 ${
                          currentStep > step.number ? 'bg-success text-white' :
                          currentStep === step.number ? 'bg-primary text-white' :
                          step.isValid ? 'bg-success text-white' : 'bg-light border text-muted'
                        }`}
                        style={{ width: '24px', height: '24px', fontSize: '10px' }}
                      >
                        {currentStep > step.number || step.isValid ? <FiCheck size={10} /> : step.number}
                      </div>
                      <div className={`small fw-semibold ${currentStep === step.number ? 'text-primary' : 'text-muted'}`} style={{ fontSize: '10px', lineHeight: '1.1' }}>
                        {step.title}
                      </div>
                    </div>
                    {step.number < steps.length && (
                      <div className={`mx-1 ${step.isValid ? 'bg-success' : 'bg-light'}`} style={{ height: '1px', flex: '1' }}></div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            
            {/* Scrollable Content */}
            <form onSubmit={handleSubmit} className="d-flex flex-column" style={{ height: 'calc(100vh - 120px)' }}>
              <div className="flex-grow-1" style={{ 
                overflowY: 'auto',
                minHeight: '0',
                maxHeight: 'calc(100vh - 180px)'
              }}>
                <div className="p-2">
                  
                  {/* Step 1: Selection */}
                  {currentStep === 1 && (
                    <div className="row g-2">
                      {/* Patient Selection - Compact */}
                      <div className="col-12">
                        <div className={`card border ${selectedPatient ? 'border-success' : 'border-light'}`}>
                          <div className="card-header bg-light d-flex align-items-center justify-content-between py-1">
                            <div className="d-flex align-items-center gap-1">
                              <FiUsers className="text-primary" size={14} />
                              <small className="mb-0 fw-medium">Patient {!preSelectedPatientId && <span className="text-danger">*</span>}</small>
                            </div>
                            {selectedPatient && <span className="badge bg-success badge-sm">✓</span>}
                          </div>
                          <div className="card-body p-2">
                            {!preSelectedPatientId ? (
                              <div className="dropdown">
                                <button
                                  className="btn btn-outline-primary btn-sm dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                                  type="button"
                                  onClick={() => setShowPatientDropdown(!showPatientDropdown)}
                                  disabled={loadingPatients}
                                  style={{ fontSize: '0.8rem' }}
                                >
                                  <span>
                                    {loadingPatients ? 'Loading...' : selectedPatient ? selectedPatient.name : 'Select patient'}
                                  </span>
                                  <FiChevronDown size={12} />
                                </button>
                                
                                {showPatientDropdown && (
                                  <div className="dropdown-menu show w-100" style={{ maxHeight: '150px', overflowY: 'auto', zIndex: 1060 }}>
                                    {patients.length > 0 ? (
                                      patients.map(patient => (
                                        <button
                                          key={patient._id || patient.id}
                                          type="button"
                                          className="dropdown-item d-flex align-items-center gap-2 py-1"
                                          onClick={() => handlePatientSelect(patient)}
                                          style={{ fontSize: '0.8rem' }}
                                        >
                                          <FiUser className="text-muted" size={12} />
                                          <div>
                                            <div className="fw-medium">{patient.name}</div>
                                            {patient.email && (
                                              <small className="text-muted" style={{ fontSize: '0.7rem' }}>{patient.email}</small>
                                            )}
                                          </div>
                                        </button>
                                      ))
                                    ) : (
                                      <div className="dropdown-item text-muted">No patients</div>
                                    )}
                                  </div>
                                )}
                              </div>
                            ) : (
                              selectedPatient && (
                                <div className="alert alert-success d-flex align-items-center gap-2 mb-0 py-1">
                                  <FiCheck className="text-success" size={12} />
                                  <div>
                                    <strong style={{ fontSize: '0.8rem' }}>{selectedPatient.name}</strong>
                                    {selectedPatient.email && (
                                      <div className="text-muted" style={{ fontSize: '0.7rem' }}>{selectedPatient.email}</div>
                                    )}
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Doctor Selection - Compact */}
                      <div className="col-12">
                        <div className={`card border ${selectedDoctor ? 'border-success' : 'border-light'}`}>
                          <div className="card-header bg-light d-flex align-items-center justify-content-between py-1">
                            <div className="d-flex align-items-center gap-1">
                              <FiAward className="text-primary" size={14} />
                              <small className="mb-0 fw-medium">Doctor <span className="text-danger">*</span></small>
                            </div>
                            {selectedDoctor && <span className="badge bg-success badge-sm">✓</span>}
                          </div>
                          <div className="card-body p-2">
                            <div className="dropdown">
                              <button
                                className="btn btn-outline-primary btn-sm dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                                type="button"
                                onClick={() => setShowDoctorDropdown(!showDoctorDropdown)}
                                disabled={loadingDoctors}
                                style={{ fontSize: '0.8rem' }}
                              >
                                <span>
                                  {loadingDoctors ? 'Loading...' : selectedDoctor ? `${selectedDoctor.name} ${selectedDoctor.surname}` : 'Select doctor'}
                                </span>
                                <FiChevronDown size={12} />
                              </button>
                              
                              {showDoctorDropdown && (
                                <div className="dropdown-menu show w-100" style={{ maxHeight: '150px', overflowY: 'auto', zIndex: 1060 }}>
                                  {doctors.length > 0 ? (
                                    doctors.map(doctor => (
                                      <button
                                        key={doctor._id}
                                        type="button"
                                        className="dropdown-item d-flex align-items-center gap-2 py-1"
                                        onClick={() => handleDoctorSelect(doctor)}
                                        style={{ fontSize: '0.8rem' }}
                                      >
                                        <FiAward className="text-muted" size={12} />
                                        <div>
                                          <div className="fw-medium">{doctor.name} {doctor.surname}</div>
                                          {doctor.specialty && (
                                            <small className="text-muted" style={{ fontSize: '0.7rem' }}>{doctor.specialty}</small>
                                          )}
                                        </div>
                                      </button>
                                    ))
                                  ) : (
                                    <div className="dropdown-item text-muted">No doctors</div>
                                  )}
                                </div>
                              )}
                            </div>
                            
                            {selectedDoctor && (
                              <div className="mt-1 p-1 bg-light rounded" style={{ fontSize: '0.7rem' }}>
                                <strong>Specialty:</strong> {selectedDoctor.specialty || 'Not specified'}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Treatment Selection - Compact */}
                      <div className="col-12">
                        <div className={`card border ${selectedTreatmentTemplate ? 'border-success' : 'border-light'}`}>
                          <div className="card-header bg-light d-flex align-items-center justify-content-between py-1">
                            <div className="d-flex align-items-center gap-1">
                              <FiActivity className="text-primary" size={14} />
                              <small className="mb-0 fw-medium">Treatment <span className="text-danger">*</span></small>
                            </div>
                            {selectedTreatmentTemplate && <span className="badge bg-success badge-sm">✓</span>}
                          </div>
                          <div className="card-body p-2">
                            <div className="dropdown">
                              <button
                                className="btn btn-outline-primary btn-sm dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                                type="button"
                                onClick={() => setShowTreatmentDropdown(!showTreatmentDropdown)}
                                disabled={loadingTreatments}
                                style={{ fontSize: '0.8rem' }}
                              >
                                <span>
                                  {loadingTreatments ? 'Loading...' : selectedTreatmentTemplate ? selectedTreatmentTemplate.name : 'Select treatment'}
                                </span>
                                <FiChevronDown size={12} />
                              </button>
                              
                              {showTreatmentDropdown && (
                                <div className="dropdown-menu show w-100" style={{ maxHeight: '200px', overflowY: 'auto', zIndex: 1060 }}>
                                  {availableTreatments.length > 0 ? (
                                    availableTreatments.map(treatment => (
                                      <button
                                        key={treatment.id}
                                        type="button"
                                        className="dropdown-item py-1 border-bottom"
                                        onClick={() => handleTreatmentSelect(treatment)}
                                        style={{ fontSize: '0.8rem' }}
                                      >
                                        <div className="d-flex align-items-start gap-2">
                                          <FiActivity className="text-muted mt-1" size={12} />
                                          <div className="flex-grow-1">
                                            <div className="fw-medium">{treatment.name}</div>
                                            <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                                              {treatment.protocol_code} • {treatment.phases_count} phases • {treatment.estimated_duration_days} days
                                            </div>
                                            <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                                              {treatment.specialty_name}
                                            </div>
                                          </div>
                                        </div>
                                      </button>
                                    ))
                                  ) : (
                                    <div className="dropdown-item text-muted">No treatments available</div>
                                  )}
                                </div>
                              )}
                            </div>
                            
                            {selectedTreatmentTemplate && (
                              <div className="mt-1 p-1 bg-light rounded" style={{ fontSize: '0.7rem' }}>
                                <div><strong>Protocol:</strong> {selectedTreatmentTemplate.protocol_code}</div>
                                <div><strong>Duration:</strong> ~{selectedTreatmentTemplate.estimated_duration_days} days</div>
                                <div><strong>Phases:</strong> {selectedTreatmentTemplate.phases_count}</div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 2: Treatment Details */}
                  {currentStep === 2 && (
                    <div className="row g-2">
                      {/* Basic Treatment Info */}
                      <div className="col-12">
                        <div className="card">
                          <div className="card-header bg-light py-1">
                            <small className="mb-0 fw-medium d-flex align-items-center gap-1">
                              <FiCalendar size={14} />
                              Treatment Schedule
                            </small>
                          </div>
                          <div className="card-body p-2">
                            <div className="row g-2">
                              <div className="col-6">
                                <label className="form-label" style={{ fontSize: '0.8rem' }}>
                                  Start Date <span className="text-danger">*</span>
                                </label>
                                <input
                                  type="date"
                                  className="form-control form-control-sm"
                                  value={formData.treatment_info.start_date}
                                  onChange={(e) => handleFormChange('start_date', e.target.value, 'treatment_info')}
                                  min={new Date().toISOString().split('T')[0]}
                                  style={{ fontSize: '0.8rem' }}
                                />
                              </div>
                              <div className="col-6">
                                <label className="form-label" style={{ fontSize: '0.8rem' }}>
                                  Estimated End Date
                                </label>
                                <input
                                  type="date"
                                  className="form-control form-control-sm"
                                  value={formData.treatment_info.estimated_end_date}
                                  onChange={(e) => handleFormChange('estimated_end_date', e.target.value, 'treatment_info')}
                                  style={{ fontSize: '0.8rem' }}
                                  readOnly={selectedTreatmentTemplate && selectedTreatmentTemplate.estimated_duration_days}
                                />
                              </div>
                            </div>
                            
                            <div className="row g-2 mt-1">
                              <div className="col-6">
                                <label className="form-label" style={{ fontSize: '0.8rem' }}>Status</label>
                                <select
                                  className="form-select form-select-sm"
                                  value={formData.treatment_info.status}
                                  onChange={(e) => handleFormChange('status', e.target.value, 'treatment_info')}
                                  style={{ fontSize: '0.8rem' }}
                                >
                                  <option value="pending">Pending</option>
                                  <option value="active">Active</option>
                                  <option value="on_hold">On Hold</option>
                                </select>
                              </div>
                              <div className="col-6">
                                <label className="form-label" style={{ fontSize: '0.8rem' }}>Progress %</label>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={formData.treatment_info.progress_percentage}
                                  onChange={(e) => handleFormChange('progress_percentage', parseInt(e.target.value) || 0, 'treatment_info')}
                                  min="0"
                                  max="100"
                                  style={{ fontSize: '0.8rem' }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Consent Status */}
                      <div className="col-12">
                        <div className="card">
                          <div className="card-header bg-light py-1">
                            <small className="mb-0 fw-medium d-flex align-items-center gap-1">
                              <FiLock size={14} />
                              Consent Management
                            </small>
                          </div>
                          <div className="card-body p-2">
                            <div className="form-check form-check-sm mb-1">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={formData.consent_status.is_required}
                                onChange={(e) => handleFormChange('is_required', e.target.checked, 'consent_status')}
                                id="consentRequired"
                              />
                              <label className="form-check-label" htmlFor="consentRequired" style={{ fontSize: '0.8rem' }}>
                                Consent Required
                              </label>
                            </div>
                            
                            {formData.consent_status.is_required && (
                              <>
                                <div className="form-check form-check-sm mb-1">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={formData.consent_status.is_signed}
                                    onChange={(e) => {
                                      handleFormChange('is_signed', e.target.checked, 'consent_status');
                                      handleFormChange('pending_signature', !e.target.checked, 'consent_status');
                                    }}
                                    id="consentSigned"
                                  />
                                  <label className="form-check-label" htmlFor="consentSigned" style={{ fontSize: '0.8rem' }}>
                                    Consent Already Signed
                                  </label>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Next Actions */}
                      <div className="col-12">
                        <div className="card">
                          <div className="card-header bg-light d-flex align-items-center justify-content-between py-1">
                            <small className="mb-0 fw-medium d-flex align-items-center gap-1">
                              <FiClock size={14} />
                              Next Actions ({formData.next_actions.length})
                            </small>
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm"
                              onClick={addNextAction}
                              style={{ fontSize: '0.7rem', padding: '1px 6px' }}
                            >
                              <FiPlus size={10} /> Add
                            </button>
                          </div>
                          <div className="card-body p-2">
                            {formData.next_actions.length === 0 ? (
                              <small className="text-muted">No actions scheduled</small>
                            ) : (
                              <div className="d-flex flex-column gap-1">
                                {formData.next_actions.map((action, index) => (
                                  <div key={index} className="border rounded p-2 bg-light">
                                    <div className="row g-1">
                                      <div className="col-4">
                                        <select
                                          className="form-select form-select-sm"
                                          value={action.action_type}
                                          onChange={(e) => updateNextAction(index, 'action_type', e.target.value)}
                                          style={{ fontSize: '0.7rem' }}
                                        >
                                          <option value="consultation">Consultation</option>
                                          <option value="test">Test</option>
                                          <option value="medication">Medication</option>
                                          <option value="follow_up">Follow-up</option>
                                        </select>
                                      </div>
                                      <div className="col-4">
                                        <input
                                          type="date"
                                          className="form-control form-control-sm"
                                          value={action.scheduled_date}
                                          onChange={(e) => updateNextAction(index, 'scheduled_date', e.target.value)}
                                          style={{ fontSize: '0.7rem' }}
                                        />
                                      </div>
                                      <div className="col-3">
                                        <select
                                          className="form-select form-select-sm"
                                          value={action.priority}
                                          onChange={(e) => updateNextAction(index, 'priority', e.target.value)}
                                          style={{ fontSize: '0.7rem' }}
                                        >
                                          <option value="low">Low</option>
                                          <option value="normal">Normal</option>
                                          <option value="high">High</option>
                                          <option value="urgent">Urgent</option>
                                        </select>
                                      </div>
                                      <div className="col-1">
                                        <button
                                          type="button"
                                          className="btn btn-outline-danger btn-sm"
                                          onClick={() => removeNextAction(index)}
                                          style={{ fontSize: '0.7rem', padding: '1px 4px' }}
                                        >
                                          <FiX size={10} />
                                        </button>
                                      </div>
                                    </div>
                                    <div className="mt-1">
                                      <textarea
                                        className="form-control form-control-sm"
                                        rows="1"
                                        placeholder="Action description..."
                                        value={action.description}
                                        onChange={(e) => updateNextAction(index, 'description', e.target.value)}
                                        style={{ fontSize: '0.7rem' }}
                                      />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Active Alerts */}
                      <div className="col-12">
                        <div className="card">
                          <div className="card-header bg-light d-flex align-items-center justify-content-between py-1">
                            <small className="mb-0 fw-medium d-flex align-items-center gap-1">
                              <FiBell size={14} />
                              Initial Alerts ({formData.active_alerts.length})
                            </small>
                            <button
                              type="button"
                              className="btn btn-outline-warning btn-sm"
                              onClick={addAlert}
                              style={{ fontSize: '0.7rem', padding: '1px 6px' }}
                            >
                              <FiPlus size={10} /> Add
                            </button>
                          </div>
                          <div className="card-body p-2">
                            {formData.active_alerts.length === 0 ? (
                              <small className="text-muted">No alerts configured</small>
                            ) : (
                              <div className="d-flex flex-column gap-1">
                                {formData.active_alerts.map((alert, index) => (
                                  <div key={index} className="border rounded p-2 bg-light">
                                    <div className="row g-1">
                                      <div className="col-4">
                                        <select
                                          className="form-select form-select-sm"
                                          value={alert.alert_type}
                                          onChange={(e) => updateAlert(index, 'alert_type', e.target.value)}
                                          style={{ fontSize: '0.7rem' }}
                                        >
                                          <option value="information">Information</option>
                                          <option value="warning">Warning</option>
                                          <option value="critical">Critical</option>
                                          <option value="reminder">Reminder</option>
                                        </select>
                                      </div>
                                      <div className="col-4">
                                        <select
                                          className="form-select form-select-sm"
                                          value={alert.priority}
                                          onChange={(e) => updateAlert(index, 'priority', e.target.value)}
                                          style={{ fontSize: '0.7rem' }}
                                        >
                                          <option value="low">Low</option>
                                          <option value="medium">Medium</option>
                                          <option value="high">High</option>
                                          <option value="urgent">Urgent</option>
                                        </select>
                                      </div>
                                      <div className="col-3">
                                        <div className="form-check form-check-sm">
                                          <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={alert.requires_action}
                                            onChange={(e) => updateAlert(index, 'requires_action', e.target.checked)}
                                            id={`alertAction${index}`}
                                          />
                                          <label className="form-check-label" htmlFor={`alertAction${index}`} style={{ fontSize: '0.7rem' }}>
                                            Action Required
                                          </label>
                                        </div>
                                      </div>
                                      <div className="col-1">
                                        <button
                                          type="button"
                                          className="btn btn-outline-danger btn-sm"
                                          onClick={() => removeAlert(index)}
                                          style={{ fontSize: '0.7rem', padding: '1px 4px' }}
                                        >
                                          <FiX size={10} />
                                        </button>
                                      </div>
                                    </div>
                                    <div className="mt-1">
                                      <textarea
                                        className="form-control form-control-sm"
                                        rows="1"
                                        placeholder="Alert message..."
                                        value={alert.message}
                                        onChange={(e) => updateAlert(index, 'message', e.target.value)}
                                        style={{ fontSize: '0.7rem' }}
                                      />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 3: Review */}
                  {currentStep === 3 && (
                    <div className="row g-2">
                      {/* Treatment Summary */}
                      <div className="col-12">
                        <div className="card border-success">
                          <div className="card-header bg-success text-white py-1">
                            <small className="mb-0 fw-medium d-flex align-items-center gap-1">
                              <FiInfo size={14} />
                              Treatment Summary
                            </small>
                          </div>
                          <div className="card-body p-2">
                            <div className="row g-2">
                              <div className="col-6">
                                <div className="d-flex align-items-center gap-1 mb-1">
                                  <FiUsers className="text-primary" size={12} />
                                  <small className="fw-medium">Patient</small>
                                </div>
                                <div className="p-2 bg-light rounded">
                                  <div className="fw-medium" style={{ fontSize: '0.8rem' }}>
                                    {selectedPatient?.name || 'Unknown Patient'}
                                  </div>
                                  {selectedPatient?.email && (
                                    <small className="text-muted">{selectedPatient.email}</small>
                                  )}
                                </div>
                              </div>
                              
                              <div className="col-6">
                                <div className="d-flex align-items-center gap-1 mb-1">
                                  <FiAward className="text-primary" size={12} />
                                  <small className="fw-medium">Doctor</small>
                                </div>
                                <div className="p-2 bg-light rounded">
                                  <div className="fw-medium" style={{ fontSize: '0.8rem' }}>
                                    {selectedDoctor ? `${selectedDoctor.name} ${selectedDoctor.surname}` : 'Unknown Doctor'}
                                  </div>
                                  {selectedDoctor?.specialty && (
                                    <small className="text-muted">{selectedDoctor.specialty}</small>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            <div className="mt-2">
                              <div className="d-flex align-items-center gap-1 mb-1">
                                <FiActivity className="text-primary" size={12} />
                                <small className="fw-medium">Treatment</small>
                              </div>
                              <div className="p-2 bg-light rounded">
                                <div className="fw-medium" style={{ fontSize: '0.8rem' }}>
                                  {selectedTreatmentTemplate?.name || 'Unknown Treatment'}
                                </div>
                                <div className="d-flex justify-content-between align-items-center mt-1">
                                  <small className="text-muted">
                                    {selectedTreatmentTemplate?.protocol_code} • {selectedTreatmentTemplate?.phases_count} phases
                                  </small>
                                  <small className="text-muted">
                                    ~{selectedTreatmentTemplate?.estimated_duration_days} days
                                  </small>
                                </div>
                              </div>
                            </div>

                            <div className="row g-2 mt-2">
                              <div className="col-6">
                                <small className="text-muted">Start Date:</small>
                                <div className="fw-medium" style={{ fontSize: '0.8rem' }}>
                                  {formData.treatment_info.start_date || 'Not set'}
                                </div>
                              </div>
                              <div className="col-6">
                                <small className="text-muted">Status:</small>
                                <div>
                                  <span className={`badge ${
                                    formData.treatment_info.status === 'active' ? 'bg-success' :
                                    formData.treatment_info.status === 'pending' ? 'bg-warning' :
                                    'bg-secondary'
                                  }`} style={{ fontSize: '0.7rem' }}>
                                    {formData.treatment_info.status.charAt(0).toUpperCase() + formData.treatment_info.status.slice(1)}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {formData.next_actions.length > 0 && (
                              <div className="mt-2">
                                <small className="text-muted">Next Actions: {formData.next_actions.length}</small>
                                <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                                  {formData.next_actions.slice(0, 2).map((action, idx) => (
                                    <div key={idx}>• {action.action_type} {action.scheduled_date && `(${action.scheduled_date})`}</div>
                                  ))}
                                  {formData.next_actions.length > 2 && (
                                    <div>• ... and {formData.next_actions.length - 2} more</div>
                                  )}
                                </div>
                              </div>
                            )}

                            {formData.active_alerts.length > 0 && (
                              <div className="mt-2">
                                <small className="text-muted">Initial Alerts: {formData.active_alerts.length}</small>
                                <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                                  {formData.active_alerts.slice(0, 2).map((alert, idx) => (
                                    <div key={idx}>• {alert.alert_type} ({alert.priority})</div>
                                  ))}
                                  {formData.active_alerts.length > 2 && (
                                    <div>• ... and {formData.active_alerts.length - 2} more</div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Validation Status */}
                      <div className="col-12">
                        <div className={`alert ${isFormValid ? 'alert-success' : 'alert-warning'} d-flex align-items-center gap-2 mb-2 py-2`}>
                          {isFormValid ? (
                            <>
                              <FiCheck className="text-success" />
                              <div>
                                <strong style={{ fontSize: '0.8rem' }}>Ready to Create</strong>
                                <div style={{ fontSize: '0.7rem' }}>All required information has been provided.</div>
                              </div>
                            </>
                          ) : (
                            <>
                              <FiAlertTriangle className="text-warning" />
                              <div>
                                <strong style={{ fontSize: '0.8rem' }}>Missing Information</strong>
                                <div style={{ fontSize: '0.7rem' }}>
                                  {!selectedPatient && '• Select a patient'}<br />
                                  {!selectedDoctor && '• Select a doctor'}<br />
                                  {!selectedTreatmentTemplate && '• Select a treatment template'}<br />
                                  {!formData.treatment_info.start_date && '• Set start date'}
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Sticky Footer with Navigation */}
              <div className="border-top bg-light p-2 flex-shrink-0">
                <div className="d-flex justify-content-between align-items-center">
                  {/* Back Button */}
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                    disabled={currentStep === 1}
                    style={{ fontSize: '0.8rem' }}
                  >
                    ← Back
                  </button>
                  
                  {/* Step Info */}
                  <small className="text-muted">
                    Step {currentStep} of {steps.length}
                  </small>
                  
                  {/* Next/Submit Button */}
                  {currentStep < 3 ? (
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => setCurrentStep(currentStep + 1)}
                      disabled={!steps[currentStep - 1].isValid}
                      style={{ fontSize: '0.8rem' }}
                    >
                      Next →
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className="btn btn-success btn-sm d-flex align-items-center gap-1"
                      disabled={!isFormValid || isSubmitting}
                      style={{ fontSize: '0.8rem' }}
                    >
                      {isSubmitting ? (
                        <>
                          <div className="spinner-border spinner-border-sm" role="status" style={{ width: '12px', height: '12px' }}>
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          Creating...
                        </>
                      ) : (
                        <>
                          <FiSave size={12} />
                          Create Treatment
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </>
      )}
    </>
  );
};

export default EnhancedTreatmentForm;